import { Button, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CustomDatePicker } from '@planning/components/CustomDatePicker'
import { CustomTimePicker } from '@planning/components/CustomTimePicker'
import { getDateTimeUtcString } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FormField, useFormStore } from '@tom-ui/utils'
import { Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'

type VisitTimingDialogProps = {
  initialValues?: Partial<VisitTimingDialogInitialValues>
  timingType: 'ATA' | 'ATB' | 'ATD' | 'ATO' | 'ATC'
  onSubmit: (date?: string | null) => Promise<void>
  noCloseAfterConfirm?: boolean
}

type VisitTimingDialogInitialValues = { date: Dayjs | null; time: Dayjs | null }

export const VisitTimingDialog: FC<VisitTimingDialogProps> = observer(props => {
  const { initialValues, timingType, onSubmit, noCloseAfterConfirm } = props
  const { dialogStore, appViewStore } = usePlanningStore()
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const formStore = useFormStore<VisitTimingDialogInitialValues>()

  const title = useMemo(() => {
    if (timingType === 'ATA') {
      return `${t('setAta', 'Set ATA')}?`
    } else if (timingType === 'ATB') {
      return `${t('setAtb', 'Set ATB')}?`
    } else if (timingType === 'ATD') {
      return `${t('setAtd', 'Set ATD')}?`
    } else if (timingType === 'ATO') {
      return `${t('setAto', 'Set ATO')}?`
    } else if (timingType === 'ATC') {
      return `${t('setAtc', 'Set ATC')}?`
    }
  }, [timingType, t])

  const warning = useMemo(() => {
    if (timingType === 'ATA') {
      return t('setTheActualTimeOfArrivalATA', 'Set the actual time of arrival (ATA)')
    } else if (timingType === 'ATB') {
      return t('setTheActualTimeOfBerthingATB', 'Set the actual time of berthing (ATB)')
    } else if (timingType === 'ATD') {
      return t('setTheActualTimeOfDepartureATD', 'Set the actual time of departure (ATD)')
    } else if (timingType === 'ATO') {
      return t(
        'setTheActualTimeOfOperationStartATO',
        'Set the actual time of operation start (ATO)',
      )
    } else if (timingType === 'ATC') {
      return t(
        'setTheActualTimeOfOperationCompletionATC',
        'Set the actual time of operation completion (ATC)',
      )
    }
  }, [timingType, t])

  const submitButtonLabel = useMemo(() => {
    if (timingType === 'ATA') {
      return t('yesSetAta', 'Yes, set ATA')
    } else if (timingType === 'ATB') {
      return t('yesSetAtb', 'Yes, set ATB')
    } else if (timingType === 'ATD') {
      return t('yesSetAtd', 'Yes, set ATD')
    } else if (timingType === 'ATO') {
      return t('yesSetAto', 'Yes, set ATO')
    } else if (timingType === 'ATC') {
      return t('yesSetAtc', 'Yes, set ATC')
    }
  }, [timingType, t])

  return (
    <>
      <DialogTitle title={title}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant='body1' color={theme.palette.text.secondary}>
          {warning}.
        </Typography>

        <Grid container spacing={1} mb={1} mt={1}>
          <Grid size={{ xs: 6 }} width='100%'>
            <FormField
              formStore={formStore}
              name='date'
              initialValue={initialValues?.date}
              validate={value => (value?.isValid() ? undefined : t('fieldIsRequired'))}
            >
              {store => (
                <>
                  <CustomDatePicker
                    {...store.props}
                    label={t('date', 'Date')}
                    value={store.value ?? undefined}
                    error={!!store.error}
                    helperText={t('fieldIsRequired')}
                    onChange={val => store.setValue(val)}
                    required={true}
                  />
                  {!store.error && (
                    <Typography variant='caption' color='text.secondary'>
                      DD/MM/YYYY
                    </Typography>
                  )}
                </>
              )}
            </FormField>
          </Grid>

          <Grid size={{ xs: 6 }} width='100%'>
            <FormField
              formStore={formStore}
              name='time'
              initialValue={initialValues?.date}
              validate={value => (value?.isValid() ? undefined : t('fieldIsRequired'))}
            >
              {store => (
                <>
                  <CustomTimePicker
                    {...store.props}
                    label={t('time', 'Time')}
                    ampm={false}
                    fullWidth
                    required
                    helperText={t('fieldIsRequired')}
                    onChange={val => store.setValue(val)}
                    error={!!store.error}
                    value={store.value ?? undefined}
                  />
                  {!store.error && (
                    <Typography variant='caption' color='text.secondary'>
                      {t('24hrClock', '24hr clock')}
                    </Typography>
                  )}
                </>
              )}
            </FormField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          color='primary'
          data-cy={`set-${timingType.toLowerCase()}-confirm-btn`}
          onClick={async () => {
            try {
              if (!formStore.validate()) return

              await onSubmit(getDateTimeUtcString(formStore.data.date, formStore.data.time))
              if (noCloseAfterConfirm) return
              dialogStore.close()
            } catch (error: any) {
              appViewStore.setShowAlert(
                'error',
                error?.response?.data?.detail ?? 'Failed to set timing',
              )
            }
          }}
        >
          {submitButtonLabel}
        </Button>
        <Button
          variant='text'
          color='inherit'
          onClick={() => {
            dialogStore.close()
          }}
        >
          {t('cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </>
  )
})
